import { useEffect, useState } from 'react';

/**
 * Phones get the mobile layout: narrow screens, or touch screens held in
 * landscape (wide but very short). Tablets keep the desktop layout, which
 * still understands touch gestures on the map.
 */
const PHONE_QUERY = '(max-width: 760px), (pointer: coarse) and (max-height: 520px)';
const OVERRIDE_KEY = 'dayz-editor:layout';

export type LayoutMode = 'auto' | 'mobile' | 'desktop';

function readOverride(): LayoutMode {
  try {
    const param = new URLSearchParams(window.location.search).get('layout');
    if (param === 'mobile' || param === 'desktop') return param;
    const stored = localStorage.getItem(OVERRIDE_KEY);
    return stored === 'mobile' || stored === 'desktop' ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

export function useLayout(): { mobile: boolean; mode: LayoutMode; setMode: (mode: LayoutMode) => void } {
  const [mode, setModeState] = useState<LayoutMode>(readOverride);
  const [phone, setPhone] = useState(() => window.matchMedia?.(PHONE_QUERY).matches ?? false);

  useEffect(() => {
    const query = window.matchMedia?.(PHONE_QUERY);
    if (!query) return;
    const update = () => setPhone(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const setMode = (next: LayoutMode) => {
    try {
      if (next === 'auto') localStorage.removeItem(OVERRIDE_KEY);
      else localStorage.setItem(OVERRIDE_KEY, next);
    } catch {
      /* ignore - the choice just will not survive a reload */
    }
    setModeState(next);
  };

  return { mobile: mode === 'auto' ? phone : mode === 'mobile', mode, setMode };
}
